import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '@rma/dashboard/authentication/public/api-authentication';
import {
  AvailableProfiles,
  AvailableProfilesRequest,
  ContextSelectorService,
  CurrentUserService,
  DashboardProfileContextService,
  ProfileIdentifier,
} from '@rma/dashboard/authentication/public/user';
import { RmaLocalStorageKey, RmaLocalStorageService } from '@rma/dashboard/generic/util/local-storage';
import { HardNavigationService } from '@rma/generic/util/posthog';
import { catchError, EMPTY, Observable, skip, take, tap } from 'rxjs';

interface State {
  isLoading: boolean;
  availableProfiles: AvailableProfiles;
  profileUsageHistory: ProfileIdentifier[];
  searchTerm?: string;
  selectedTabIndex: number;
  hasLoadingError?: boolean;
}

export const TAKE = 10;
const profileUsageHistoryKey: RmaLocalStorageKey = 'rmaProfileUsageHistory';
const tabIndexKey: RmaLocalStorageKey = 'rmaSwitchAccountTabIndex';
const INITIAL_PROFILES = { results: [], skip: 0, take: TAKE, total: 0 };

@Injectable()
export class SwitchAccountFacade {
  private readonly authenticationService = inject(AuthService);
  private readonly contextSelector = inject(ContextSelectorService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly localStorage = inject(RmaLocalStorageService);
  private readonly profileContextService = inject(DashboardProfileContextService);
  private readonly hardNavigation = inject(HardNavigationService);

  private readonly state = signal<State>({
    isLoading: false,
    availableProfiles: INITIAL_PROFILES,
    profileUsageHistory: [],
    selectedTabIndex: this.getLastSelectedTab(),
  });

  private readonly availableProfiles = computed(() => this.state().availableProfiles);
  private readonly profileUsageHistory = computed(() => this.state().profileUsageHistory);

  public readonly currentProfile = toSignal(this.profileContextService.profileIdentifier$, { requireSync: true });
  public readonly defaultProfile = this.currentUserService.currentUserDefaultProfile;

  public readonly hasMore = computed(() => this.availableProfiles().total > this.availableProfiles().results.length);
  public readonly isAssisting = toSignal(this.profileContextService.isAssistant$, { requireSync: true });
  public readonly loading = computed(() => this.state().isLoading);
  public readonly loadingError = computed(() => this.state().hasLoadingError);
  public readonly profileType = computed(() => (this.state().selectedTabIndex === 0 ? 'Agent' : 'Agency'));
  public readonly profiles = computed(() => this.availableProfiles().results);
  public readonly searchTerm = computed(() => this.state().searchTerm ?? '');
  public readonly selectedTabIndex = computed(() => this.state().selectedTabIndex);

  public constructor() {
    this.updateState({ profileUsageHistory: this.getProfileUsageHistory() });
    this.loadProfiles();
  }

  public setSearchTerm(term: string): void {
    this.updateState({
      searchTerm: term,
      isLoading: true,
      hasLoadingError: false,
    });
    this.loadProfiles();
  }

  public loadMore() {
    const loaded = this.availableProfiles().results.length;
    const { total } = this.availableProfiles();

    if (loaded > total || loaded === total) {
      return;
    }

    this.updateState({ isLoading: true, hasLoadingError: false });
    const profileType = this.profileType();

    this.currentUserService
      .getAvailableProfiles(profileType, this.getFilters(this.state().availableProfiles.skip + TAKE))
      .pipe(catchError((error) => this.handleRequestError(`loading more profiles`, error)))
      .subscribe((availableProfiles) =>
        this.updateState({
          isLoading: false,
          hasLoadingError: false,
          availableProfiles: {
            ...this.availableProfiles(),
            skip: availableProfiles.skip,
            results: [...this.availableProfiles().results, ...availableProfiles.results],
          },
        }),
      );
  }

  public switchAccount(profile: ProfileIdentifier): Observable<string> {
    const { profileType, profileCode } = profile;
    this.contextSelector.setContext({ profileType, profileCode });

    return this.profileContextService.profileContextChanged$.pipe(
      skip(1), // there is always an initial emission when the context is set
      take(1),
      tap(() => this.updateProfileUsageHistory(profile)),
    );
  }

  public logout(): void {
    this.authenticationService
      .logout()
      .pipe(take(1))
      .subscribe(() => {
        this.hardNavigation.assign('/');
      });
  }

  public selectTab(selectedTabIndex: number): void {
    this.updateState({ selectedTabIndex, searchTerm: '' });
    this.loadProfiles();
    this.localStorage.setItem(tabIndexKey, selectedTabIndex);
  }

  private loadProfiles() {
    const profiles$ = this.currentUserService.getAvailableProfiles(this.profileType(), this.getFilters(0));

    this.updateState({
      availableProfiles: INITIAL_PROFILES,
      isLoading: true,
      hasLoadingError: false,
    });

    profiles$.pipe(catchError((error) => this.handleRequestError('loading profile', error))).subscribe((availableProfiles) =>
      this.updateState({
        isLoading: false,
        hasLoadingError: false,
        availableProfiles,
      }),
    );
  }

  private updateState(newState: Partial<State>) {
    this.state.update((state) => ({ ...state, ...newState }));
  }

  private getProfileUsageHistory(): ProfileIdentifier[] {
    const history = this.localStorage.getArray<ProfileIdentifier>(profileUsageHistoryKey);

    if (!history?.length) {
      return [this.currentProfile()];
    }

    return history;
  }

  private getLastSelectedTab(): number {
    return this.localStorage.getNumber(tabIndexKey) ?? 0;
  }

  private updateProfileUsageHistory(profile: ProfileIdentifier): void {
    const history = this.profileUsageHistory();

    const profileUsageHistory = history.filter(
      ({ profileType, profileCode }) => `${profileType}${profileCode}` !== `${profile.profileType}${profile.profileCode}`,
    );

    profileUsageHistory.unshift(profile);

    this.updateState({ profileUsageHistory });
    this.localStorage.setItem(profileUsageHistoryKey, profileUsageHistory);
  }

  private getFilters(skipAmount?: number): AvailableProfilesRequest {
    return {
      skip: skipAmount,
      take: TAKE,
      searchTerm: this.state().searchTerm,
      orderFirst: this.profileUsageHistory(),
      adminToAgentPermission: this.searchTerm() ? undefined : 'Core',
    };
  }

  private handleRequestError(message: string, error: unknown) {
    console.error(message, error);
    this.updateState({ isLoading: false, hasLoadingError: true });
    return EMPTY;
  }
}
